import fileMD5 from '@/utils/fileMD5.js';
import Vue from 'vue';
import translator from '@/locales';
import transformToCamelCase from '@/api/shared/transformers/toCamelCaseTransformer';

class FileUploader {
  constructor({ url, onProgress = () => {} } = {}) {
    this.reset();
    this.url = url;
    this.onProgress = onProgress;
  }

  async startUpload(file) {
    if (!file) {
      throw new Error('File not found');
    }

    this.reset();
    this.file = file;
    this.hash = await fileMD5(this.file);

    // get fileId and chunk size
    await this.createUpload();

    return this.checkAndUpload();
  }

  async checkAndUpload() {
    if (this.aborted) return;

    const endByte = Math.min(this.offset + this.chunkSize, this.file.size);
    const chunk = this.file.slice(this.offset, endByte);

    try {
      const { status, body } = await this.uploadChunk(chunk);

      this.offset = body.offset;
      this.onProgress(endByte, this.file.size);

      if (status === 201) {
        return transformToCamelCase(body);
      }

      return this.checkAndUpload();
    } catch (error) {
      if (error.status === 400) {
        this.offset = error.body?.offset;
        return this.checkAndUpload();
      }

      const errorMessage = error.body?.message || error.message;
      throw {
        message: errorMessage || translator.t('UNKNOWN_ERROR'),
        errors: error.body?.errors,
      };
    }
  }

  async createUpload() {
    const params = {
      file_hash: this.hash,
      size: this.file.size,
      file_name: this.file.name,
    };

    try {
      const { body = {} } = await Vue.http.get(this.url, { params });
      this.chunkSize = body.chunk_size;
      this.fileID = body.file_id;
      this.offset = Math.min(body.offset, this.file.size);

      this.onProgress(this.offset, this.file.size);
    } catch ({ body = {}, message }) {
      const errorMessage = body.message || message;

      throw {
        message: errorMessage || translator.t('UNKNOWN_ERROR'),
        errors: body.errors,
      };
    }
  }

  async uploadChunk(chunk) {
    const hash = await fileMD5(chunk);

    const params = {
      offset: this.offset,
      chunk_size: chunk.size,
      size: this.file.size,
      chunk_hash: hash,
    };

    const uploadChunkUrl = this.url + this.fileID + '/';
    return Vue.http.patch(uploadChunkUrl, chunk, {
      params,
      headers: { 'Content-Type': 'application/octet-stream' },
    });
  }

  abort() {
    this.aborted = true;
  }

  reset() {
    this.aborted = false;
    this.file = null;
    this.fileID = null;
    this.uploading = false;
    this.hash = null;
    this.offset = 0;
    this.chunkSize = null;
  }
}

export default FileUploader;
